import { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, Play, RefreshCw, Loader2, AlertTriangle, CheckCircle2,
  Package, KeyRound, FileText,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import "@/pages/analytics/CommunityIntelligence.css";
import "@/pages/analytics/AlertsCenter.css";

export default function SecurityCenter() {
  const [posture, setPosture] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [p, h] = await Promise.all([
        api.get("/admin/security/posture"),
        api.get("/admin/security/findings?limit=50"),
      ]);
      setPosture(p.data);
      setHistory(h.data.findings || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runPenTest = async () => {
    setRunning("pen_test"); setError("");
    try {
      const { data } = await api.post("/admin/security/pen-test/run");
      setSelected(data);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setRunning(null); }
  };

  const runSupplyScan = async () => {
    setRunning("supply"); setError("");
    try {
      const { data } = await api.post("/admin/security/supply-chain/snapshot");
      setSelected(data);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setRunning(null); }
  };

  return (
    <div className="hw-ci" data-testid="security-center">
      <div className="hw-ci__eyebrow">
        <ShieldCheck className="w-3.5 h-3.5"
                       style={{ display: "inline", verticalAlign: "text-bottom",
                                 marginRight: "0.3rem" }} />
        Enterprise Security Posture
      </div>
      <h1 className="hw-ci__title">Security posture at a glance</h1>
      <p className="hw-ci__sub">
        Run automated pen-test simulations, dependency scans, and secret audits.
        Findings feed the SOC 2-aligned readiness evidence chain.
      </p>

      {/* Identity Security & Governance cross-link (Iter 74.5) */}
      <a
        href="/identity-security"
        data-testid="security-identity-link"
        className="inline-flex items-center gap-2 mt-2 mb-2 text-sm text-[#1E3A8A] hover:underline"
      >
        Identity Security &amp; Governance dashboard →
      </a>
      {/* Security Lifecycle cross-link (Iter 74.7) */}
      <a
        href="/admin/security-lifecycle"
        data-testid="security-lifecycle-link"
        className="inline-flex items-center gap-2 mb-4 ml-4 text-sm text-[#1E3A8A] hover:underline"
      >
        Security Lifecycle (cert / key rotation) →
      </a>
      {/* Partner Integration Applications cross-link (Iter 74.8) */}
      <a
        href="/admin/partner-applications"
        data-testid="partner-applications-link"
        className="inline-flex items-center gap-2 mb-4 ml-4 text-sm text-[#1E3A8A] hover:underline"
      >
        Partner Integration Applications & Onboarding →
      </a>

      <div className="hw-ci__controls">
        <button className="hw-ci__btn hw-ci__btn--primary"
                onClick={runPenTest} disabled={!!running}
                data-testid="run-pen-test">
          {running === "pen_test" ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</>
          ) : (<><Play className="w-3.5 h-3.5" /> Run pen-test simulation</>)}
        </button>
        <button className="hw-ci__btn"
                onClick={runSupplyScan} disabled={!!running}
                data-testid="run-supply-scan">
          {running === "supply" ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning…</>
          ) : (<><Package className="w-3.5 h-3.5" /> Supply-chain snapshot</>)}
        </button>
        <button className="hw-ci__btn" onClick={load} data-testid="security-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {error && <div style={{ color: "#B91C1C" }} data-testid="security-error">{error}</div>}
      {loading && !posture && (
        <div style={{ color: "#0F766E" }}>
          <Loader2 className="w-4 h-4 animate-spin"
                    style={{ display: "inline", verticalAlign: "text-bottom" }} /> loading…
        </div>
      )}

      {posture && (
        <>
          <div className="hw-ci__kpi-grid" data-testid="security-posture-kpis">
            <Kpi label="Pen-test score"
                  value={posture.pen_test?.score_pct != null ? `${posture.pen_test.score_pct}%` : "—"}
                  testid="posture-pentest-score" />
            <Kpi label="Passed" value={posture.pen_test?.passed ?? "—"} testid="posture-passed" />
            <Kpi label="Failed" value={posture.pen_test?.failed ?? "—"} testid="posture-failed" />
            <Kpi label="Weak secrets"
                  value={posture.secrets?.weak ?? "—"}
                  testid="posture-weak-secrets" />
            <Kpi label="Python scan"
                  value={posture.supply_chain?.python_status || "—"}
                  testid="posture-python" />
            <Kpi label="Node scan"
                  value={posture.supply_chain?.node_status || "—"}
                  testid="posture-node" />
          </div>

          <div className="hw-ci__panel" data-testid="secrets-inventory">
            <h2>
              <span>
                <KeyRound className="w-4 h-4"
                            style={{ display: "inline", verticalAlign: "text-bottom",
                                      marginRight: "0.3rem" }} />
                Secrets audit
              </span>
              <small>{posture.secrets?.total || 0} monitored keys</small>
            </h2>
            <table className="hw-ci__table">
              <thead>
                <tr>
                  <th>Key</th><th>Present</th><th>Issues</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(posture.secrets?.details || []).map((s) => (
                  <tr key={s.key} data-testid={`secret-row-${s.key}`}>
                    <td><strong>{s.key}</strong></td>
                    <td>{s.present ? "✓" : "—"}</td>
                    <td>
                      {(s.issues || []).length === 0 ? (
                        <span style={{ color: "#059669" }}>OK</span>
                      ) : (
                        (s.issues || []).map((i) => (
                          <span key={i} className="hw-alerts__chip">{i}</span>
                        ))
                      )}
                    </td>
                    <td>
                      {s.ok ? (
                        <CheckCircle2 className="w-4 h-4"
                                        style={{ color: "#059669", display: "inline" }} />
                      ) : (
                        <AlertTriangle className="w-4 h-4"
                                          style={{ color: "#B45309", display: "inline" }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selected && (
        <div className="hw-ci__panel"
              data-testid="finding-detail"
              style={{ borderColor: "#0F766E", borderWidth: 2 }}>
          <h2>
            <span>
              <FileText className="w-4 h-4"
                          style={{ display: "inline", verticalAlign: "text-bottom",
                                    marginRight: "0.3rem" }} />
              {selected.kind === "pen_test" ? "Pen-test findings" : "Supply-chain snapshot"}
              {" · "}{(selected.run_at || "").slice(0, 16).replace("T", " ")}
            </span>
            <button className="hw-ci__btn" onClick={() => setSelected(null)}>Close</button>
          </h2>
          {selected.kind === "pen_test" && (
            <table className="hw-ci__table">
              <thead>
                <tr>
                  <th>Scenario</th><th>Expected</th><th>Actual</th>
                  <th>Sev</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(selected.findings || []).map((f) => (
                  <tr key={f.id} data-testid={`finding-row-${f.id}`}>
                    <td>{f.scenario}</td>
                    <td style={{ fontSize: "0.75rem" }}>{f.expected}</td>
                    <td style={{ fontSize: "0.75rem" }}>{String(f.actual)}</td>
                    <td>
                      <span className={`hw-alerts__severity hw-alerts__severity--${f.severity}`}>
                        {f.severity}
                      </span>
                    </td>
                    <td>
                      {f.status === "PASS" ? (
                        <span style={{ color: "#059669", fontWeight: 700 }}>PASS</span>
                      ) : (
                        <span style={{ color: "#B91C1C", fontWeight: 700 }}>FAIL</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selected.kind === "supply_chain_snapshot" && (
            <div style={{ fontSize: "0.8rem" }}>
              <p>Python: <strong>{selected.python?.status}</strong> · advisories: {(selected.python?.advisories || []).length}</p>
              <p>Node: <strong>{selected.node?.status}</strong> · advisories: {(selected.node?.advisories || []).length}</p>
              <p>Secrets: {selected.secrets?.weak} weak of {selected.secrets?.total}</p>
            </div>
          )}
        </div>
      )}

      <div className="hw-ci__panel" data-testid="findings-history">
        <h2>
          <span>Findings history</span>
          <small>{history.length}</small>
        </h2>
        <table className="hw-ci__table">
          <thead>
            <tr>
              <th>Kind</th><th>Ran at</th><th>Score</th><th>Findings</th><th></th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id} data-testid={`history-row-${h.id}`}>
                <td><strong>{h.kind}</strong></td>
                <td style={{ fontSize: "0.75rem", color: "#64748B" }}>
                  {(h.run_at || "").slice(0, 16).replace("T", " ")}
                </td>
                <td>{h.score_pct != null ? `${h.score_pct}%` : "—"}</td>
                <td>{h.total ?? (h.python ? "supply" : "—")}</td>
                <td>
                  <button className="hw-ci__btn"
                          onClick={() => setSelected(h)}
                          data-testid={`history-view-${h.id}`}>View</button>
                </td>
              </tr>
            ))}
            {history.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center",
                                                padding: "0.9rem", color: "#64748B" }}>
                No findings yet — run a pen-test or supply-chain snapshot.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="hw-ci__kpi" data-testid={testid}>
      <div className="hw-ci__kpi-label">{label}</div>
      <div className="hw-ci__kpi-value">{value ?? "—"}</div>
    </div>
  );
}
